import { Controller } from "@hotwired/stimulus"
import { refreshPhone } from "../studio_phone"

// The studio's "Your spots" (30 Oct 2026): every place on the page, filtered
// in the browser — a city or neighbourhood chip, a type (the styled select's
// hidden input, spots_kind), the words typed — shown forty at a time. A row's
// eye hides a place their content names (or shows it again), its minus takes
// off one added by hand, without leaving the list; then the phone swaps its
// places in, without reloading.
export default class extends Controller {
  static targets = ["row", "chip", "query", "count", "more", "empty"]

  connect() {
    this.where = ""
    this.limit = 40
    this.filter()
  }

  pick(event) {
    this.where = event.currentTarget.dataset.value
    this.chipTargets.forEach((chip) => chip.classList.toggle("on", chip === event.currentTarget))
    this.limit = 40
    this.filter()
  }

  more() {
    this.limit += 40
    this.filter()
  }

  filter() {
    const kind = this.element.querySelector("input[name=spots_kind]")?.value || ""
    const words = this.hasQueryTarget ? this.fold(this.queryTarget.value).split(/\s+/).filter(Boolean) : []
    let matched = 0
    this.rowTargets.forEach((row) => {
      const ok = (!this.where || row.dataset.where === this.where) &&
                 (!kind || row.dataset.kind === kind) &&
                 words.every((word) => row.dataset.q.includes(word))
      if (ok) matched += 1
      row.hidden = !ok || matched > this.limit
    })
    const shown = Math.min(matched, this.limit)
    if (this.hasCountTarget) this.countTarget.textContent = matched ? `${shown} of ${matched}` : ""
    if (this.hasMoreTarget) this.moreTarget.hidden = matched <= this.limit
    if (this.hasEmptyTarget) this.emptyTarget.hidden = matched > 0
  }

  // The eye: hidden from the page or shown again, the row dimmed or not.
  async toggle(event) {
    const button = event.currentTarget
    const row = button.closest("[data-spots-list-target=row]")
    const hide = !row.classList.contains("off")
    button.disabled = true
    const token = document.querySelector("meta[name=csrf-token]")?.content
    const body = new FormData()
    body.append("hidden", hide ? "1" : "0")
    const response = await fetch(button.dataset.url, {
      method: "PATCH", body, headers: { "X-CSRF-Token": token }, credentials: "same-origin"
    }).catch(() => null)
    button.disabled = false
    if (!response?.ok) return
    row.classList.toggle("off", hide)
    button.setAttribute("aria-pressed", String(hide))
    button.title = hide ? "Show again" : "Hide from my page"
    refreshPhone(Date.now(), { part: "places" })
  }

  // The minus: a place added by hand, taken off the page.
  async remove(event) {
    const button = event.currentTarget
    const row = button.closest("[data-spots-list-target=row]")
    button.disabled = true
    const token = document.querySelector("meta[name=csrf-token]")?.content
    const response = await fetch(button.dataset.url, {
      method: "DELETE", headers: { "X-CSRF-Token": token, Accept: "application/json" }, credentials: "same-origin"
    }).catch(() => null)
    if (!response?.ok) { button.disabled = false; return }
    row.remove()
    this.recount()
    this.filter()
    refreshPhone(Date.now(), { part: "places" })
  }

  // The chips' counts follow the rows left (after a minus).
  recount() {
    const counts = {}
    this.rowTargets.forEach((row) => { counts[row.dataset.where] = (counts[row.dataset.where] || 0) + 1 })
    this.chipTargets.forEach((chip) => {
      const em = chip.querySelector("em")
      if (em) em.textContent = chip.dataset.value ? (counts[chip.dataset.value] || 0) : this.rowTargets.length
    })
  }

  fold(text) {
    return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
  }
}
